import { useState } from 'react';
import { api, fileToBase64 } from '../../lib/api';
import { addDays, dayRange, startOfWeek, today } from '../../lib/dates';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { setQuery, useLocation } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, cx, DataTable, Empty, ErrorBox, Field, Input, Loading, Modal, PageHeader, Select, Stat, useToast } from '../../ui/components';
import { groupByDay } from '../../ui/lessons';
import { RRuleInput } from '../../ui/schema';

export function AttendanceList({ sessionId, canRemove, onChanged }: { sessionId: string; canRemove?: boolean; onChanged?: () => void }) {
  const gym = useGym();
  const i18n = useI18n();
  const data = useApi(() => api(`${T(gym.slug)}/sessions/${sessionId}/attendees`), [sessionId]);
  if (data.error) return <ErrorBox error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Loading />;
  const set = async (bookingId: string, status: string): Promise<void> => {
    await api(`${T(gym.slug)}/bookings/${bookingId}/attendance`, { body: { status } });
    data.reload();
    onChanged?.();
  };
  const people: any[] = data.data.attendees;
  if (!people.length) return <Empty>{i18n.t('attendance.nobody')}</Empty>;
  return (
    <div className="stack-sm">
      {people.map((a) => (
        <div key={a.bookingId} className={cx('attendee', a.status)}>
          <div className="attendee-main">
            <strong>{a.name}</strong>
            <span className="row gap-sm wrap">
              <Badge value={a.status} />
              {a.isTrial && <span className="badge badge-warn">{i18n.t('attendance.trial')}</span>}
              {!a.isTrial && a.firstTimer && <span className="badge badge-warn">{i18n.t('attendance.first_time')}</span>}
              {a.healthFlag && <span className="badge badge-bad" title={a.healthNotes ?? ''}>⚕ {i18n.t('attendance.health')}</span>}
            </span>
            {a.healthNotes && <small className="health-note">{a.healthNotes}</small>}
            {a.emergencyContacts?.length > 0 && (
              <details><summary className="small">{i18n.t('attendance.ice')}</summary>
                {a.emergencyContacts.map((c: any, i: number) => <div key={i} className="small">{c.name} ({c.relation}) <a href={`tel:${c.phone}`}>{c.phone}</a></div>)}
              </details>
            )}
          </div>
          {a.status !== 'waitlisted' && (
            <div className="row gap-sm">
              <AsyncButton size="sm" variant={a.status === 'attended' ? 'primary' : 'secondary'} onClick={() => set(a.bookingId, a.status === 'attended' ? 'booked' : 'attended')}>✓ {i18n.t('attendance.present')}</AsyncButton>
              <AsyncButton size="sm" variant={a.status === 'no_show' ? 'danger' : 'secondary'} onClick={() => set(a.bookingId, a.status === 'no_show' ? 'booked' : 'no_show')}>✗ {i18n.t('attendance.absent')}</AsyncButton>
              {canRemove && a.status === 'booked' && (
                <AsyncButton size="sm" variant="ghost" confirm={i18n.t('attendance.remove_confirm')} onClick={async () => { await api(`${T(gym.slug)}/admin/bookings/${a.bookingId}/cancel`, { body: {} }); data.reload(); onChanged?.(); }}>{i18n.t('common.remove')}</AsyncButton>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function TrainerLessonsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const tz = gym.gym.timezone;
  const start = query.get('week') ?? startOfWeek(today(tz), gym.gym.policies.weekStartsOn ?? 1);
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/trainer/sessions`, { query: dayRange(start, addDays(start, 7), tz) }), [start]);
  const [open, setOpen] = useState<any | null>(null);
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.my_lessons')} actions={
        <div className="row gap-sm">
          <Button variant="secondary" size="sm" onClick={() => setQuery({ week: addDays(start, -7) })}>‹</Button>
          <span>{i18n.day(start, { day: 'numeric', month: 'short' })} – {i18n.day(addDays(start, 6), { day: 'numeric', month: 'short' })}</span>
          <Button variant="secondary" size="sm" onClick={() => setQuery({ week: addDays(start, 7) })}>›</Button>
        </div>
      } />
      {list.error ? <ErrorBox error={list.error} /> : !list.data ? <Loading /> : list.data.length === 0 ? <Empty icon="🏋">{i18n.t('trainer.no_lessons')}</Empty> : (
        groupByDay(list.data.map((s) => ({ ...s, startsAt: s.starts_at })), tz).map(([day, sessions]) => (
          <section key={day} className="day-group">
            <h3 className="day-title">{i18n.day(day, { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
            {sessions.map((s) => (
              <button type="button" key={s.id} className="lesson-row" style={{ borderLeftColor: s.lesson_color ?? 'var(--brand)' }} onClick={() => setOpen(s)}>
                <div className="lesson-time"><strong>{i18n.time(s.starts_at)}</strong><small>{i18n.time(s.ends_at)}</small></div>
                <div className="lesson-main"><strong>{s.lesson_name}</strong><small className="muted">{[s.location_name, s.room_name].filter(Boolean).join(' · ')}</small></div>
                <div className="lesson-right">
                  {s.status !== 'scheduled' ? <Badge value={s.status} /> : <span className="badge badge-neutral">{s.booked_count}/{s.capacity}</span>}
                </div>
              </button>
            ))}
          </section>
        ))
      )}
      {open && (
        <Modal title={`${open.lesson_name} · ${i18n.time(open.starts_at)}`} onClose={() => setOpen(null)} footer={
          open.status === 'scheduled' && open.starts_at > new Date().toISOString()
            ? <AsyncButton variant="ghost" confirm={i18n.t('trainer.release_confirm')} onClick={async () => { await api(`${T(gym.slug)}/trainer/sessions/${open.id}/release`, { body: {} }); setOpen(null); list.reload(); }}>{i18n.t('trainer.release')}</AsyncButton>
            : undefined
        }>
          <AttendanceList sessionId={open.id} onChanged={list.reload} />
        </Modal>
      )}
    </div>
  );
}

export function OpenLessonsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/trainer/open-sessions`), []);
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.open_lessons')} subtitle={i18n.t('trainer.open_help')} />
      {!list.data ? <Loading /> : list.data.length === 0 ? <Empty icon="✓">{i18n.t('trainer.no_open')}</Empty> : (
        <DataTable rows={list.data} columns={[
          { key: 'startsAt', label: i18n.t('planner.when'), render: (s) => <>{i18n.date(s.startsAt, { weekday: 'short', day: 'numeric', month: 'short' })} {i18n.time(s.startsAt)}{s.urgent && <span className="badge badge-bad"> {i18n.t('trainer.urgent')}</span>}</> },
          { key: 'lessonName', label: i18n.t('planner.lesson') },
          { key: 'locationName', label: i18n.t('planner.where') },
          { key: 'booked', label: i18n.t('planner.attendees'), render: (s) => `${s.booked}/${s.capacity}` },
          { key: 'feeCents', label: i18n.t('trainer.fee'), render: (s) => i18n.money(s.feeCents) },
          { key: 'action', label: '', render: (s) => (s.requested ? <Badge value="requested" /> : s.canClaim ? (
            <AsyncButton size="sm" onClick={async () => {
              const r = await api(`${T(gym.slug)}/trainer/sessions/${s.id}/claim`, { body: {} });
              toast.ok(i18n.t(r.assigned ? 'trainer.claimed' : 'trainer.requested'));
              list.reload();
            }}>{i18n.t('trainer.claim')}</AsyncButton>
          ) : <span className="small muted">{(s.reasons ?? []).map((r: string) => i18n.t(`fit.${r}`)).join(', ')}</span>) },
        ]} />
      )}
    </div>
  );
}

export function AvailabilityEditor({ personId, adminMode }: { personId?: string; adminMode?: boolean }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const base = adminMode ? `${T(gym.slug)}/admin/r/availability_rules` : `${T(gym.slug)}/trainer/availability`;
  const list = useApi(async () => (adminMode ? (await api(base, { query: { person_id: personId, pageSize: 200 } })).items : api<any[]>(base)), [personId]);
  const blank = { kind: 'available', start_date: today(gym.gym.timezone), end_date: '', start_time: '09:00', end_time: '17:00', rrule: { freq: 'weekly', interval: 1, byweekday: [1, 2, 3, 4, 5] } as any, note: '' };
  const [v, setV] = useState(blank);
  const weekdays = (r: any): string => (r?.byweekday ?? []).map((d: number) => i18n.day(`2024-01-0${d === 0 ? 7 : d}`, { weekday: 'short' })).join(', ');
  return (
    <div className="stack">
      <Card title={i18n.t('availability.add')}>
        <div className="grid-3">
          <Field label={i18n.field('kind')}><Select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })} options={['available', 'unavailable'].map((k) => ({ value: k, label: i18n.option(k) }))} /></Field>
          <Field label={i18n.field('start_time')}><Input type="time" value={v.start_time} onChange={(e) => setV({ ...v, start_time: e.target.value })} /></Field>
          <Field label={i18n.field('end_time')}><Input type="time" value={v.end_time} onChange={(e) => setV({ ...v, end_time: e.target.value })} /></Field>
          <Field label={i18n.field('start_date')}><Input type="date" value={v.start_date} onChange={(e) => setV({ ...v, start_date: e.target.value })} /></Field>
          <Field label={i18n.field('end_date')} hint={i18n.t('availability.end_hint')}><Input type="date" value={v.end_date} onChange={(e) => setV({ ...v, end_date: e.target.value })} /></Field>
          <Field label={i18n.field('note')}><Input value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} /></Field>
        </div>
        <Field label={i18n.t('availability.repeat')}><RRuleInput value={v.rrule} onChange={(r) => setV({ ...v, rrule: r })} /></Field>
        <AsyncButton onClick={async () => {
          await api(base, { body: { ...v, end_date: v.end_date || null, person_id: personId } });
          toast.ok(i18n.t('common.saved'));
          setV(blank);
          list.reload();
        }}>{i18n.t('common.add')}</AsyncButton>
      </Card>
      <Card title={i18n.t('availability.rules')}>
        {!list.data ? <Loading /> : <DataTable rows={list.data} columns={[
          { key: 'kind', label: i18n.field('kind'), render: (r) => <Badge value={r.kind} /> },
          { key: 'time', label: i18n.t('availability.time'), render: (r) => `${r.start_time}–${r.end_time}` },
          { key: 'repeat', label: i18n.t('availability.repeat'), render: (r) => (r.rrule ? `${i18n.t(`repeat.${r.rrule.freq}`)} ${weekdays(r.rrule)}` : i18n.t('repeat.none')) },
          { key: 'period', label: i18n.t('availability.period'), render: (r) => `${i18n.day(r.start_date)} – ${r.end_date ? i18n.day(r.end_date) : '∞'}` },
          { key: 'note', label: i18n.field('note') },
          { key: 'x', label: '', render: (r) => <AsyncButton variant="ghost" size="sm" onClick={async () => { await api(`${base}/${r.id}`, { method: 'DELETE' }); list.reload(); }}>{i18n.t('common.delete')}</AsyncButton> },
        ]} empty={i18n.t('availability.none')} />}
      </Card>
    </div>
  );
}

export function AvailabilityPage() {
  const i18n = useI18n();
  return <div className="page"><PageHeader title={i18n.t('nav.availability')} subtitle={i18n.t('availability.help')} /><AvailabilityEditor /></div>;
}

export function EarningsView({ path }: { path: string }) {
  const i18n = useI18n();
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const data = useApi(() => api(path, { query: { period } }), [period, path]);
  const shift = (n: number): void => {
    const [y, m] = period.split('-').map(Number);
    setPeriod(new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7));
  };
  return (
    <div className="stack">
      <div className="row gap-sm">
        <Button variant="secondary" size="sm" onClick={() => shift(-1)}>‹</Button>
        <strong>{i18n.day(`${period}-01`, { month: 'long', year: 'numeric' })}</strong>
        <Button variant="secondary" size="sm" onClick={() => shift(1)}>›</Button>
      </div>
      {!data.data ? <Loading /> : (
        <>
          <div className="stats">
            <Stat label={i18n.t('earnings.completed')} value={i18n.money(data.data.completedCents, data.data.currency)} />
            <Stat label={i18n.t('earnings.planned')} value={i18n.money(data.data.plannedCents, data.data.currency)} />
            <Stat label={i18n.t('earnings.lessons')} value={data.data.lines.length} />
            {data.data.statement && <Stat label={i18n.t('earnings.statement')} value={<Badge value={data.data.statement.status} />} hint={data.data.statement.invoice_number} />}
          </div>
          <DataTable rows={data.data.lines} rowKey="sessionId" columns={[
            { key: 'startsAt', label: i18n.t('planner.when'), render: (l) => `${i18n.date(l.startsAt, { day: 'numeric', month: 'short' })} ${i18n.time(l.startsAt)}` },
            { key: 'lesson', label: i18n.t('planner.lesson') },
            { key: 'location', label: i18n.t('planner.where') },
            { key: 'minutes', label: i18n.t('earnings.minutes') },
            { key: 'status', label: i18n.field('status'), render: (l) => <Badge value={l.status} /> },
            { key: 'feeCents', label: i18n.t('trainer.fee'), render: (l) => <>{i18n.money(l.feeCents, data.data.currency)}{l.estimated ? <small className="muted"> ({i18n.t('earnings.estimate')})</small> : null}</> },
          ]} empty={i18n.t('earnings.none')} />
        </>
      )}
    </div>
  );
}

export function EarningsPage() {
  const gym = useGym();
  const i18n = useI18n();
  return <div className="page"><PageHeader title={i18n.t('nav.earnings')} /><EarningsView path={`${T(gym.slug)}/trainer/earnings`} /></div>;
}

const DOC_TYPES = ['certificate', 'first_aid', 'vog', 'insurance', 'id_check', 'diploma', 'other'];

export function TrainerProfilePage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const data = useApi(() => api(`${T(gym.slug)}/trainer/profile`), []);
  const [p, setP] = useState<Record<string, any> | null>(null);
  const [doc, setDoc] = useState({ doc_type: 'certificate', name: '', valid_until: '', file: null as File | null });
  if (data.error) return <div className="page"><ErrorBox error={data.error} /></div>;
  if (!data.data) return <Loading />;
  const profile = data.data.profile;
  if (!profile) return <div className="page"><Empty>{i18n.t('trainer.no_profile')}</Empty></div>;
  const v = p ?? profile;
  const fields = ['bio', 'specialisations', 'company_name', 'company_registration', 'vat_number', 'account_holder', 'max_hours_week', 'travel_buffer_min'];
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.trainer_profile')} subtitle={i18n.option(profile.employment_type)} />
      <Card title={i18n.t('trainer.details')}>
        <div className="grid-2">
          {fields.map((f) => (
            <Field key={f} label={i18n.field(f)}>
              <Input type={['max_hours_week', 'travel_buffer_min'].includes(f) ? 'number' : 'text'} value={v[f] ?? ''} onChange={(e) => setP({ ...v, [f]: e.target.value })} />
            </Field>
          ))}
          <Field label={i18n.field('iban')} hint={profile.iban_last4 ? `•••• ${profile.iban_last4}` : undefined}><Input autoComplete="off" onChange={(e) => setP({ ...v, iban: e.target.value })} /></Field>
          <Field label={i18n.field('vat_status')}>
            <Select value={v.vat_status ?? ''} placeholder="—" onChange={(e) => setP({ ...v, vat_status: e.target.value })} options={['charges_vat', 'small_business_exempt', 'not_applicable'].map((x) => ({ value: x, label: i18n.option(x) }))} />
          </Field>
        </div>
        <AsyncButton disabled={!p} onClick={async () => {
          const patch: Record<string, any> = {};
          for (const k of [...fields, 'iban', 'vat_status']) if (p && p[k] !== profile[k] && p[k] !== undefined) patch[k] = ['max_hours_week', 'travel_buffer_min'].includes(k) ? (p[k] === '' ? null : Number(p[k])) : p[k];
          await api(`${T(gym.slug)}/trainer/profile`, { method: 'PATCH', body: patch });
          toast.ok(i18n.t('common.saved'));
          setP(null);
          data.reload();
        }}>{i18n.t('common.save')}</AsyncButton>
      </Card>
      <Card title={i18n.t('trainer.documents')}>
        <DataTable rows={data.data.documents} columns={[
          { key: 'name', label: i18n.field('name') },
          { key: 'doc_type', label: i18n.field('doc_type'), render: (d) => i18n.option(d.doc_type) },
          { key: 'valid_until', label: i18n.field('valid_until'), render: (d) => (d.valid_until ? <span className={d.valid_until < today(gym.gym.timezone) ? 'warn' : ''}>{i18n.day(d.valid_until)}</span> : '—') },
        ]} empty={i18n.t('trainer.no_documents')} />
        <div className="grid-3">
          <Field label={i18n.field('doc_type')}><Select value={doc.doc_type} onChange={(e) => setDoc({ ...doc, doc_type: e.target.value })} options={DOC_TYPES.map((d) => ({ value: d, label: i18n.option(d) }))} /></Field>
          <Field label={i18n.field('name')}><Input value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })} /></Field>
          <Field label={i18n.field('valid_until')}><Input type="date" value={doc.valid_until} onChange={(e) => setDoc({ ...doc, valid_until: e.target.value })} /></Field>
        </div>
        <Field label={i18n.t('trainer.file')} hint={i18n.t('trainer.file_hint')}><input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setDoc({ ...doc, file: e.target.files?.[0] ?? null })} /></Field>
        <AsyncButton disabled={!doc.name} onClick={async () => {
          const body: Record<string, unknown> = { doc_type: doc.doc_type, name: doc.name, valid_until: doc.valid_until || null };
          if (doc.file) Object.assign(body, { contentType: doc.file.type, dataBase64: await fileToBase64(doc.file) });
          await api(`${T(gym.slug)}/trainer/documents`, { body });
          setDoc({ doc_type: 'certificate', name: '', valid_until: '', file: null });
          data.reload();
        }}>{i18n.t('trainer.upload')}</AsyncButton>
      </Card>
    </div>
  );
}

